import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Icon, Spinner, Text, Title, notification } from "components/common";
import PageLayout from "components/common/pageLayout/pageLayout";
import DemoDataToggle from "components/custom/demoDataToggle";
import NotificationItem from "components/custom/notificationItem";
import {
  useLazyGetNotificationsQuery,
  useMarkNotificationsReadMutation,
  useAcceptFollowRequestMutation,
  useGetFollowRequestsQuery,
  useRejectFollowRequestMutation,
} from "features/community/apis";
import { demoNotifications } from "features/community/mockData/social";
import styles from "./Notifications.module.less";

const NotificationSkeleton = () => <div className={styles.skeleton} aria-label="Loading notifications">{[1, 2, 3, 4].map((key) => <div key={key}><i /><span /><span /></div>)}</div>;

const Notifications = () => {
  const navigate = useNavigate();
  const canUseDemoData = process.env.NODE_ENV !== "production";
  const [useDemoData, setUseDemoData] = useState(false);
  const [items, setItems] = useState([]);
  const [nextBefore, setNextBefore] = useState(null);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [handledRequestCodes, setHandledRequestCodes] = useState([]);
  const [processingRequestCode, setProcessingRequestCode] = useState(null);
  const [requestResolutions, setRequestResolutions] = useState({});
  const [trigger, { isFetching, isError }] = useLazyGetNotificationsQuery();
  const [markRead] = useMarkNotificationsReadMutation();
  const { data: followRequests, isLoading: isLoadingFollowRequests } = useGetFollowRequestsQuery(undefined, { skip: useDemoData });
  const [acceptFollowRequest, { isLoading: isAccepting }] = useAcceptFollowRequestMutation();
  const [rejectFollowRequest, { isLoading: isDeclining }] = useRejectFollowRequestMutation();

  const pendingRequestCodes = useMemo(
    () => new Set((followRequests?.data || []).map((request) => request.requester?.user_code).filter(Boolean)),
    [followRequests]
  );

  const load = useCallback(async (reset = false) => {
    const result = await trigger({ before: reset ? undefined : nextBefore, limit: 20 });
    if (result?.data) {
      setItems((current) => reset ? result.data.data || [] : [...current, ...(result.data.data || [])]);
      setNextBefore(result.data.nextBefore || null);
    }
    setInitialLoaded(true);
  }, [nextBefore, trigger]);

  useEffect(() => {
    if (useDemoData) return undefined;
    let active = true;
    trigger({ limit: 20 }).then((result) => {
      if (!active) return;
      if (result?.data) {
        setItems(result.data.data || []);
        setNextBefore(result.data.nextBefore || null);
      }
      setInitialLoaded(true);
    });
    return () => { active = false; };
  }, [trigger, useDemoData]);

  const openNotification = async (item, destination) => {
    if (useDemoData) {
      if (destination) navigate(destination);
      return;
    }
    if (!item.is_read) {
      setItems((current) => current.map((entry) => entry.notification_id === item.notification_id ? { ...entry, is_read: true } : entry));
      await markRead({ ids: [item.notification_id] });
    }
    if (destination) navigate(destination);
  };

  const markAll = async () => {
    if (useDemoData) return;
    await markRead({ all: true });
    setItems((current) => current.map((item) => ({ ...item, is_read: true })));
  };

  const requesterCodeFor = (item) => item.actor_user_code || item.payload?.actor?.user_code;
  const isRequestPending = (item) => {
    const requesterCode = requesterCodeFor(item);
    const savedResolution = item.requestResolution || item.payload?.follow_request_status;
    if (
      !requesterCode ||
      savedResolution === "accepted" ||
      savedResolution === "declined" ||
      handledRequestCodes.includes(requesterCode)
    ) return false;
    return useDemoData || (!isLoadingFollowRequests && pendingRequestCodes.has(requesterCode));
  };

  const handleFollowRequest = async (item, action) => {
    const requesterCode = requesterCodeFor(item);
    if (!requesterCode) return;

    if (useDemoData) {
      setProcessingRequestCode(requesterCode);
      setHandledRequestCodes((current) => [...current, requesterCode]);
      setRequestResolutions((current) => ({ ...current, [item.notification_id]: action }));
      setItems((current) => current.map((entry) => entry.notification_id === item.notification_id ? { ...entry, is_read: true, requestResolution: action } : entry));
      setProcessingRequestCode(null);
      return;
    }

    const request = action === "accept" ? acceptFollowRequest : rejectFollowRequest;
    setProcessingRequestCode(requesterCode);
    const result = await request(requesterCode);
    setProcessingRequestCode(null);
    if (result?.error) {
      notification.error({ message: result.error?.data?.message || "Unable to update follow request" });
      return;
    }
    setHandledRequestCodes((current) => [...current, requesterCode]);
    setRequestResolutions((current) => ({ ...current, [item.notification_id]: action }));
    setItems((current) => current.map((entry) => entry.notification_id === item.notification_id ? { ...entry, is_read: true, requestResolution: action } : entry));
    notification.success({ message: action === "accept" ? "Follow request accepted" : "Follow request declined" });
  };

  const displayedItems = (useDemoData ? demoNotifications : items).map((item) => (
    requestResolutions[item.notification_id]
      ? { ...item, requestResolution: requestResolutions[item.notification_id], is_read: true }
      : item
  ));
  return (
    <PageLayout backIcon label={<Title level={3}>Notifications</Title>}>
      <main className={styles.page}>
        <header className={styles.header}>
          <div><Title level={3}>Notifications</Title><Text>Followers and learning recommendations that need your attention.</Text></div>
          {!useDemoData && displayedItems.some((item) => !item.is_read) && <Button size="small" type="light" text="Mark all read" onClick={markAll} />}
        </header>
        <section className={styles.list} aria-label="Notifications">
          {isFetching && !initialLoaded && !useDemoData ? <NotificationSkeleton /> : isError && items.length === 0 && !useDemoData ? (
            <div className={styles.state}><Icon icon="BiSad" size={25} /><strong>Unable to load notifications</strong><Text>Try again to see your recent updates.</Text><Button type="default" text="Retry" onClick={() => load(true)} /></div>
          ) : displayedItems.length === 0 ? (
            <div className={styles.state}><Icon icon="UserNotification" size={27} /><strong>You’re all caught up</strong><Text>New followers and study recommendations will appear here.</Text></div>
          ) : <>
            {displayedItems.map((item) => <NotificationItem
              key={item.notification_id}
              item={item}
              onOpen={openNotification}
              isRequestPending={isRequestPending(item)}
              isProcessing={Boolean(processingRequestCode)}
              isAccepting={isAccepting && processingRequestCode === requesterCodeFor(item)}
              isDeclining={isDeclining && processingRequestCode === requesterCodeFor(item)}
              onAccept={(notificationItem) => handleFollowRequest(notificationItem, "accept")}
              onDecline={(notificationItem) => handleFollowRequest(notificationItem, "decline")}
            />)}
            {!useDemoData && nextBefore && <div className={styles.more}><Button type="default" text={isFetching ? "Loading…" : "Load more"} disabled={isFetching} onClick={() => load(false)} /></div>}
          </>}
        </section>
        {canUseDemoData && <DemoDataToggle value={useDemoData} onChange={setUseDemoData} label="Notification data" />}
      </main>
    </PageLayout>
  );
};

export default Notifications;
